
import * as THREE from "../../../libs/three.js/build/three.module.js";
import { Utils } from "../../utils.js";
import { chainSelfIntersects } from "../../utils/YDPolygonVolume.js";
import { buildVolumeJSON, buildVolumeGeoJSON, volumeFromJSON, volumeFromGeoJSON } from "../../utils/PolygonTransfer.js";

import { MeasurePanel } from "./MeasurePanel.js";

/**
 * PolygonVolumePanel — YDPolygonVolume（异形多边形棱柱）的属性面板。
 *
 * 与 VolumePanel 的差异：
 * - 核心控件是 zMin/zMax 数字输入（改后重建棱柱几何，裁剪即时生效——高度退化
 *   （<1cm）时采点可能全是近平面值，靠这里补救）；
 * - 保留位置/旋转显示与 visible/clip 复选框、重绘、删除；
 * - 隐藏 scale/copyRotation/copyScale/make uniform/download（对多边形棱柱无意义，
 *   缩放不改变多边形形状只做各向缩放，容易误解）。
 */
export class PolygonVolumePanel extends MeasurePanel {
	constructor(viewer, measurement, propertiesPanel) {
		super(viewer, measurement, propertiesPanel);

		let removeIconPath = Potree.resourcePath + '/icons/remove.svg';

		this.elContent = $(`
			<div class="measurement_content selectable">
				<span class="coordinates_table_container"></span>

				<table class="measurement_value_table">
					<tr>
						<th>\u03b1</th>
						<th>\u03b2</th>
						<th>\u03b3</th>
					</tr>
					<tr>
						<td align="center" id="angle_cell_alpha" style="width: 33%"></td>
						<td align="center" id="angle_cell_betta" style="width: 33%"></td>
						<td align="center" id="angle_cell_gamma" style="width: 33%"></td>
					</tr>
				</table>

				<br>
				<span style="font-weight: bold">Points: </span>
				<span name="polygon_point_count"></span>
				<span style="font-weight: bold; margin-left: 10px">Volume: </span>
				<span name="polygon_volume"></span>

				<table class="measurement_value_table" style="margin-top: 8px">
					<tr>
						<th>zMin</th>
						<th>zMax</th>
						<th>height</th>
					</tr>
					<tr>
						<td align="center" style="width: 33%">
							<input name="polygon_zmin" type="number" step="0.01" style="width: 100%"/>
						</td>
						<td align="center" style="width: 33%">
							<input name="polygon_zmax" type="number" step="0.01" style="width: 100%"/>
						</td>
						<td align="center" name="polygon_height" style="width: 33%"></td>
					</tr>
				</table>

				<li>
					<label style="whitespace: nowrap">
						<input name="polygon_visible" type="checkbox"/>
						<span>visible</span>
					</label>
					<label style="whitespace: nowrap; margin-left: 12px">
						<input name="polygon_clip" type="checkbox"/>
						<span>make clip volume</span>
					</label>
				</li>
				<li style="margin-top: 6px">
					<label style="whitespace: nowrap">
						<input name="polygon_column" type="checkbox"/>
						<span>柱状（所有点取最高/最低Z）</span>
					</label>
				</li>

				<li style="margin-top: 10px">
					<input name="polygon_redraw" type="button" value="redraw" style="width: 100%"/>
				</li>

				<li style="display: grid; grid-template-columns: auto auto; grid-column-gap: 5px; margin-top: 5px">
					<input name="polygon_export_json" type="button" value="导出 JSON"/>
					<input name="polygon_export_geojson" type="button" value="导出 GeoJSON"/>
				</li>
				<li style="margin-top: 5px">
					<input name="polygon_import" type="button" value="导入多边形（JSON/GeoJSON）" style="width: 100%"/>
					<input name="polygon_import_file" type="file" accept=".json,.geojson,application/json" style="display: none"/>
				</li>

				<div style="display: flex; margin-top: 12px">
					<span></span>
					<span style="flex-grow: 1"></span>
					<img name="remove" class="button-icon" src="${removeIconPath}" style="width: 16px; height: 16px"/>
				</div>
			</div>
		`);

		this.elRemove = this.elContent.find("img[name=remove]");
		this.elRemove.click(() => {
			this.viewer.scene.removeVolume(measurement);
		});

		this.elContent.find("input[name=polygon_redraw]").click(() => {
			measurement.update();
			this.update();
		});

		// zMin/zMax 数字输入：显示/输入的都是「世界 Z」（海拔，局部值 + position.z）。
		// 内部局部 zMin/zMax 以棱柱 Z 中点为原点（∓h/2），直接显示局部值会误导；
		// 改后立即重建几何（裁剪即时生效），update() 内部会重新居中并保持另一端世界高度
		this.elZMin = this.elContent.find("input[name=polygon_zmin]");
		this.elZMax = this.elContent.find("input[name=polygon_zmax]");
		const applyZ = () => {
			const base = measurement.position.z;
			const zMin = parseFloat(this.elZMin.val());
			const zMax = parseFloat(this.elZMax.val());
			if (Number.isNaN(zMin) || Number.isNaN(zMax)) {
				return;
			}
			let lMin = zMin - base;
			let lMax = zMax - base;
			if (lMax < lMin) {
				// 交换而不是拒绝：用户意图明确（范围对调）
				[lMin, lMax] = [lMax, lMin];
			}
			measurement.zMin = lMin;
			measurement.zMax = lMax;
			measurement.update();
			this.update();
		};
		this.elZMin.on("change", applyZ);
		this.elZMax.on("change", applyZ);

		// —— 导出 / 导入（JSON 与 GeoJSON，PolygonTransfer）——
		const downloadText = (text, filename) => {
			const blob = new Blob([text], { type: 'application/json' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			a.click();
			URL.revokeObjectURL(url);
		};

		this.elContent.find("input[name=polygon_export_json]").click(() => {
			if (chainSelfIntersects(measurement.points)) {
				// 体检 warn 不拒绝：even-odd 语义能处理，但提示用户这不是简单多边形
				console.warn('[PolygonVolumePanel] 多边形存在自交边（可能是历史数据），仍按现状导出');
			}
			downloadText(JSON.stringify(buildVolumeJSON(measurement), null, 2),
				`ydpolygon_${measurement.name}.json`);
		});

		this.elContent.find("input[name=polygon_export_geojson]").click(() => {
			try {
				downloadText(JSON.stringify(buildVolumeGeoJSON(measurement), null, 2),
					`ydpolygon_${measurement.name}.geojson`);
			} catch (err) {
				console.warn('[PolygonVolumePanel] GeoJSON 导出失败', err);
			}
		});

		const elImportFile = this.elContent.find("input[name=polygon_import_file]");
		this.elContent.find("input[name=polygon_import]").click(() => elImportFile.click());
		elImportFile.on('change', async (event) => {
			const file = event.target.files && event.target.files[0];
			if (!file || !this.viewer) {
				return;
			}
			try {
				const parsed = JSON.parse(await file.text());
				const volume = new Potree.YDPolygonVolume();
				if (parsed && (parsed.type === 'Feature' || (parsed.geometry && parsed.geometry.type === 'Polygon'))) {
					// GeoJSON：世界坐标环 → setFromWorldPoints 派生局部数据/原点居中
					const { ring, properties } = volumeFromGeoJSON(parsed);
					volume.setFromWorldPoints(ring);
					const zs = ring.map(p => p.z);
					const zMinW = Math.min(...zs);
					const zMaxW = Math.max(...zs);
					if (Number.isFinite(properties.zMin) && Number.isFinite(properties.zMax)) {
						// 导出时写入的世界高度优先
						volume.zMin = properties.zMin - volume.position.z;
						volume.zMax = properties.zMax - volume.position.z;
					} else if (zMaxW - zMinW < 0.01) {
						// 环无有效高程（GeoJSON 常见平面环）：以环 Z 为底，默认 1m 高
						volume.zMin = -0.5;
						volume.zMax = 0.5;
						console.warn('[PolygonVolumePanel] GeoJSON 环无有效高程，默认高度 1m');
					}
					if (properties.columnMode !== undefined) {
						volume.columnMode = !!properties.columnMode;
					}
					volume.update();
				} else {
					// 本库 JSON：字段与工程保存同形状
					const fields = volumeFromJSON(parsed);
					if (fields.name) {
						volume.name = fields.name;
					}
					volume.position.set(...fields.position);
					volume.rotation.set(...fields.rotation);
					volume.scale.set(...fields.scale);
					volume.clip = fields.clip;
					volume.points = fields.points;
					volume.zMin = fields.zMin;
					volume.zMax = fields.zMax;
					volume.columnMode = fields.columnMode;
					volume.update();
				}
				if (chainSelfIntersects(volume.points)) {
					console.warn('[PolygonVolumePanel] 导入的多边形存在自交边（按 even-odd 语义裁剪）');
				}
				this.viewer.scene.addVolume(volume);
				// viewer 注入后补一次 update：顶点球的 selection_changed 监听
				// 只在 update 的 _wireMarkers 里注册，缺这次点击棱柱不出顶点球
				volume.update();
				// 选中让顶点标记球立即可拖 + 面板切换到新棱柱
				this.viewer.inputHandler.setSelection([volume]);
			} catch (err) {
				console.warn('[PolygonVolumePanel] 导入失败', err);
				alert(`导入失败：${err.message || err}`);
			} finally {
				event.target.value = '';
			}
		});

		this.elCheckClip = this.elContent.find("input[name=polygon_clip]");
		this.elCheckClip.click(event => {
			this.measurement.clip = event.target.checked;
		});

		this.elCheckShow = this.elContent.find("input[name=polygon_visible]");
		this.elCheckShow.click(event => {
			this.measurement.visible = event.target.checked;
		});

		// 柱状/连线形态切换：柱状 = 平底 zMin、平顶 zMax 的直柱（外观与裁剪范围
		// 一致）；连线 = 底环逐点跟随顶点 Z（帘式贴点云）。切换即重建几何
		this.elCheckColumn = this.elContent.find("input[name=polygon_column]");
		this.elCheckColumn.click(event => {
			this.measurement.columnMode = event.target.checked;
			this.measurement.update();
		});

		this.propertiesPanel.addVolatileListener(measurement, "position_changed", this._update);
		this.propertiesPanel.addVolatileListener(measurement, "orientation_changed", this._update);
		this.propertiesPanel.addVolatileListener(measurement, "clip_changed", this._update);
		this.propertiesPanel.addVolatileListener(measurement, "visibility_changed", this._update);

		this.update();
	}

	update() {
		let elCoordiantesContainer = this.elContent.find('.coordinates_table_container');
		elCoordiantesContainer.empty();
		elCoordiantesContainer.append(this.createCoordinatesTable([this.measurement.position]));

		{
			const rotation = this.measurement.rotation;
			let angles = new THREE.Vector3(rotation.x, rotation.y, rotation.z);
			angles = angles.toArray();
			angles = angles.map(v => 180 * v / Math.PI);
			angles = angles.map(a => a.toFixed(1) + '\u00B0');

			this.elContent.find(`#angle_cell_alpha`).html(angles[0]);
			this.elContent.find(`#angle_cell_betta`).html(angles[1]);
			this.elContent.find(`#angle_cell_gamma`).html(angles[2]);
		}

		{
			// 面板显示世界 Z（海拔）：局部 zMin/zMax 原点在棱柱 Z 中点（∓h/2）
			const base = this.measurement.position.z;
			this.elZMin.val((base + this.measurement.zMin).toFixed(2));
			this.elZMax.val((base + this.measurement.zMax).toFixed(2));
			const height = this.measurement.zMax - this.measurement.zMin;
			this.elContent.find("td[name=polygon_height]").html(Utils.addCommas(height.toFixed(2)));
		}

		{
			this.elContent.find("span[name=polygon_point_count]").html(this.measurement.points.length);
			let volume = this.measurement.getVolume();
			this.elContent.find("span[name=polygon_volume]").html(Utils.addCommas(volume.toFixed(2)));
		}

		this.elContent.find("input[name=polygon_clip]").prop("checked", this.measurement.clip);
		this.elContent.find("input[name=polygon_visible]").prop("checked", this.measurement.visible);
		this.elContent.find("input[name=polygon_column]").prop("checked", this.measurement.columnMode === true);
	}
};
